import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  List,
  NumberField,
  TextField as RaTextField,
  useRecordContext,
  useTranslate,
  useSidebarState,
} from "react-admin";
import type { RaRecord } from "react-admin";
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import {
  CustomDatagrid,
  FilterAutocomplete,
  FILTER_HEIGHT,
  ListActionsBar,
  NamedDeleteButton,
} from "@/components/common";
import { useCanCreate, useCanEdit, useCanDelete } from "@/utils/permissions";
import PricingFormDialog from "./PricingFormDialog";
import {
  getAllCostCenters,
  getAllNotifyApplications,
} from "@/features/accounts/services/accountService";

const i18nBase = "resources.credit-consumption/pricing";

const CHANNELS = [
  "Email",
  "SMS",
  "WhatsApp",
  "Telegram",
  "Viber",
  "LINE",
  "Web Push",
  "App Push",
  "Slack",
  "Teams",
  "Discord",
  "Google Chat",
  "WeCom",
  "Mattermost",
  "Rocket.Chat",
  "Inbox",
  "Webhook",
];

interface RowActionContextValue {
  openEdit: (record: RaRecord) => void;
}

const RowActionContext = createContext<RowActionContextValue | null>(null);
const useRowActions = (): RowActionContextValue => {
  const ctx = useContext(RowActionContext);
  if (!ctx) {
    throw new Error("Row action used outside PricingList");
  }
  return ctx;
};

const EditRowButton: React.FC = () => {
  const record = useRecordContext<RaRecord>();
  const translate = useTranslate();
  const { openEdit } = useRowActions();
  if (!record) return null;
  return (
    <Button
      size="small"
      variant="text"
      color="primary"
      startIcon={<EditIcon fontSize="small" />}
      onClick={(e) => {
        e.stopPropagation();
        openEdit(record);
      }}
      sx={{ textTransform: "none", fontWeight: 500, minWidth: 0 }}
    >
      {translate("ra.action.edit")}
    </Button>
  );
};

interface ListActionsProps {
  onAdd: () => void;
  costCenterNames: string[];
  applicationNames: string[];
}

const ListActions: React.FC<ListActionsProps> = ({ onAdd, costCenterNames, applicationNames }) => {
  const translate = useTranslate();
  const canCreate = useCanCreate();
  return (
    <ListActionsBar
      filters={
        <>
          <FilterAutocomplete
            source="costCenterName"
            label={translate(`${i18nBase}.filters.costCenter`)}
            options={costCenterNames}
          />
          <FilterAutocomplete
            source="applicationName"
            label={translate(`${i18nBase}.filters.application`)}
            options={applicationNames}
          />
          <FilterAutocomplete
            source="communicationChannel"
            label={translate(`${i18nBase}.filters.communicationChannel`)}
            options={CHANNELS}
          />
        </>
      }
      addButton={
        canCreate ? (
          <Button
            variant="contained"
            color="primary"
            onClick={onAdd}
            startIcon={<AddIcon />}
            aria-label={translate(`${i18nBase}.actions.addPricing`)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              flexShrink: 0,
              height: FILTER_HEIGHT,
              minWidth: { xs: FILTER_HEIGHT, sm: "auto" },
              borderRadius: 1.5,
              px: { xs: 0, sm: 2.5 },
              boxShadow: "none",
              "& .MuiButton-startIcon": {
                mr: { xs: 0, sm: 1 },
                ml: { xs: 0, sm: -0.5 },
              },
              "&:hover": { boxShadow: "0 2px 6px rgba(11, 165, 183, 0.25)" },
            }}
          >
            <Box
              component="span"
              sx={{ display: { xs: "none", sm: "inline" } }}
            >
              {translate(`${i18nBase}.actions.addPricing`)}
            </Box>
          </Button>
        ) : null
      }
    />
  );
};

export const PricingList: React.FC = () => {
  const translate = useTranslate();
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [costCenterNames, setCostCenterNames] = useState<string[]>([]);
  const [applicationNames, setApplicationNames] = useState<string[]>([]);

  useEffect(() => {
    void getAllCostCenters()
      .then((list) => setCostCenterNames(list.map((c) => c.name).filter(Boolean)))
      .catch(() => undefined);
    void getAllNotifyApplications()
      .then((list) => setApplicationNames(list.map((a) => a.name).filter(Boolean)))
      .catch(() => undefined);
  }, []);
  const [sidebarOpen] = useSidebarState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const maxWidth = isMobile ? "100%" : sidebarOpen ? "85vw" : "95vw";
  const canEdit = useCanEdit();
  const canDelete = useCanDelete();

  const openEdit = (record: RaRecord): void => {
    setEditingId(record.id);
  };

  const openAdd = (): void => {
    setCreateOpen(true);
  };

  const handlers: RowActionContextValue = { openEdit };

  return (
    <RowActionContext.Provider value={handlers}>
      <Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            mb: 1,
            mt: 0,
            ml: 2,
          }}
        >
          {translate(`${i18nBase}.name`, { smart_count: 2 })}
        </Typography>
        <List
          actions={<ListActions onAdd={openAdd} costCenterNames={costCenterNames} applicationNames={applicationNames} />}
          sort={{ field: "0", order: "ASC" }}
          storeKey={false}
          disableSyncWithLocation
          perPage={10}
          exporter={false}
          sx={{
            maxWidth,
            overflow: "visible",
            "& .RaList-main, & .RaList-content": {
              overflow: "visible",
              width: "100%",
              maxWidth: "100%",
            },
            "& .RaListToolbar-root, & .RaList-actions": {
              display: "flex",
              width: "100%",
              mb: { xs: 1, sm: 2 },
              px: 0,
              py: { xs: 0, sm: 1 },
              minHeight: "auto",
            },
          }}
        >
          <CustomDatagrid
            primarySource="costCenterName"
            bulkActionButtons={false}
            disableMobileView
          >
            <RaTextField source="costCenterName" />
            <RaTextField source="applicationName" />
            <RaTextField source="communicationChannel" />
            <NumberField source="pricePerUnit" />
            {canEdit && <EditRowButton />}
            {canDelete && <NamedDeleteButton displayField="costCenterName" />}
          </CustomDatagrid>
        </List>

        <PricingFormDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          recordId={null}
        />
        <PricingFormDialog
          open={editingId !== null}
          onClose={() => setEditingId(null)}
          recordId={editingId}
        />
      </Box>
    </RowActionContext.Provider>
  );
};

export default PricingList;
